/* eslint-disable promise/always-return,promise/catch-or-return */
import React, { useEffect, useState, useMemo, Fragment, useRef } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames';

import {
  getTestResults,
  getCommonResult,
  getNotFoundSelectState,
} from '../../utils/getTestResults';
import { normalizeDevices } from '../../../../redux/opentok/helpers/utils';
import VideoVisitBtn from '../../../Healthboard/Components/VideoVisitBtn';
import { getAudioLogLevel } from '../../utils/getAudioLogLevel';
import ProgressBar from '../Progress/ProgressBar';
import ResultsCard from '../ResultsCard';
import Paper from '../../../Paper';
import Icon from '../../../Icon';
import styles from './styles.styl';

function Results({
  results,
  notFound,
  formatMessage,
  onStartVideoVisit,
  virtualCallEnabled,
}) {
  const publisher = useRef(null);
  const [level, setLevel] = useState(0);
  const [audio, setAudio] = useState('default');
  const [video, setVideo] = useState('');
  const [populated, setPopulatedStatus] = useState(false);
  const [audioList, setAudioList] = useState([]);
  const [videoList, setVideoList] = useState([]);
  const audioSupported = results && results.audio.supported;
  const videoSupported = results && results.video.supported;

  useEffect(() => {
    const { video: videoData, audio: audioData } = getNotFoundSelectState(
      formatMessage,
    );

    const hasVideo = videoSupported && !notFound;
    const hasAudio = audioSupported && !notFound;

    if (!hasAudio) {
      setAudioList([audioData]);
      setAudio(audioData.value);
    }

    if (!hasVideo) {
      setVideoList([videoData]);
      setVideo(videoData.value);
    }

    if (videoSupported || audioSupported) {
      getUserMedia().then(({ audioSourceList, videoSourceList }) => {
        if (audioSupported) {
          setAudioList(audioSourceList);
        }
        if (videoSupported) {
          setVideoList(videoSourceList);
        }

        if (!populated) {
          if (videoSupported) {
            initiateDefaultVideoSelection(videoSourceList);
          }
          if (audioSupported) {
            initiateDefaultAudioSelection(audioSourceList);
          }

          setPopulatedStatus(true);
        }
      });
    }
    return () => {
      if (publisher.current) {
        publisher.current.off('audioLevelUpdated');
        publisher.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (populated) {
      publish();
    }
  }, [populated]);

  const systemTitle = useMemo(() => {
    const { common: videoCommon, results: videoResults } = getTestResults(
      results,
      notFound,
      false,
    );

    const { common: audioCommon, results: audioResults } = getTestResults(
      results,
      notFound,
      true,
    );

    return {
      video: {
        title: `${formatMessage({
          id: videoCommon,
        })} ${formatMessage({ id: videoResults.id })}`,
        danger: videoResults.danger,
      },
      audio: {
        title: `${formatMessage({
          id: audioCommon,
        })} ${formatMessage({ id: audioResults.id })}`,
        danger: audioResults.danger,
        icon: audioResults.icon,
      },
    };
  }, [results, notFound]);

  const { title, danger } = useMemo(
    () => getCommonResult(formatMessage, results, notFound),
    [formatMessage, results],
  );

  function publish() {
    publisher.current = OT.initPublisher(
      'publisher',
      {
        audioSource: audio,
        videoSource: video,
        width: 230,
        height: 170,
      },
      err => {
        if (err) {
          console.log(`Publish error ${err.message}`);
        } else {
          setupAudioLevelMeter();
          onChange(audio, 'audio');
          onChange(video, 'video');
        }
      },
    );
  }

  function initiateDefaultVideoSelection(videoSourceList) {
    if (videoSourceList.length > 0) {
      const videoSource = get(videoSourceList, '[0].value');
      setVideo(videoSource);
    }
  }

  function initiateDefaultAudioSelection(audioSourceList) {
    const audioSource = get(audioSourceList, '[1].value');
    setAudio(audioSource);
  }

  function setupAudioLevelMeter() {
    publisher.current.on('audioLevelUpdated', event => {
      const logLevel = getAudioLogLevel(event);
      setLevel(logLevel);
    });
  }

  function getUserMedia() {
    return new Promise((resolve, reject) => {
      OT.getDevices((err, devices) => {
        if (err) {
          return reject(err);
        }

        const audioDevices = devices.filter(
          ({ kind }) => kind === 'audioInput',
        );
        const videoDevices = devices.filter(
          ({ kind }) => kind === 'videoInput',
        );

        return resolve({
          audioSourceList: normalizeDevices(audioDevices),
          videoSourceList: normalizeDevices(videoDevices),
        });
      });
    });
  }

  const onChange = (val, type) => {
    if (type === 'audio' && val !== audio) {
      publisher.current.setAudioSource(val).then(() => {
        setAudio(val);
      });
    }

    if (type === 'video' && val !== video) {
      publisher.current.cycleVideo().then(({ deviceId }) => {
        setVideo(deviceId);
      });
    }
  };

  return (
    <Paper className={styles.root} overflowVisible>
      <div className={styles.header}>
        <span className={cn(styles.title, { [styles.titleDanger]: danger })}>
          {title}
        </span>
        <VideoVisitBtn
          type="primary"
          onStartVideoVisit={onStartVideoVisit}
          disabled={!virtualCallEnabled}
        />
      </div>
      <div>
        <ResultsCard
          list={videoList}
          value={video}
          disabled={!videoList.length || notFound || !videoSupported}
          formatMessage={formatMessage}
          onChange={value => onChange(value, 'video')}
          label={formatMessage({ id: 'virtualVisit.device.video' })}
          title={systemTitle.video.title}
          subtitle={formatMessage({ id: 'virtualVisit.test.videoSubtitle' })}
          danger={systemTitle.video.danger}
        >
          <div id="publisher" />
        </ResultsCard>
        <ResultsCard
          list={audioList}
          value={audio}
          disabled={!audioList.length || notFound || !audioSupported}
          onChange={value => onChange(value, 'audio')}
          label={formatMessage({ id: 'virtualVisit.device.mic' })}
          title={systemTitle.audio.title}
          subtitle={formatMessage({ id: 'virtualVisit.test.audioSubtitle' })}
          danger={systemTitle.audio.danger}
        >
          <Fragment>
            <Icon size={32} name={systemTitle.audio.icon} className="mb2" />
            <div className={styles.progress}>
              <ProgressBar progress={level} height={12} />
            </div>
          </Fragment>
        </ResultsCard>
      </div>
    </Paper>
  );
}

Results.propTypes = {
  formatMessage: PropTypes.func,
  results: PropTypes.object,
  notFound: PropTypes.bool,
  onStartVideoVisit: PropTypes.func,
  virtualCallEnabled: PropTypes.bool,
};

export default Results;



// WEBPACK FOOTER //
// ./src/components/VirtualVideoTest/Components/Results/index.jsx